import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { GithubReviewState, reviewAgentsQuery, usePullMutations } from '@/features/pulls/services/pulls';
import { requestedReviewersQuery } from '@/features/pulls/services/pulls';
import { useZero } from '@/lib/zero';
import { collaboratorsQuery } from '@/services/repo';

export const TERMINAL_REVIEW_STATES = ['APPROVED', 'CHANGES_REQUESTED'];

export function useReviewers() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  const z = useZero();

  const [pull] = useZeroQuery(
    z.query.PullsTable.where(({ and, cmp }) => and(cmp('owner', owner), cmp('repo', repo), cmp('number', number))).one()
  );

  const [reviews] = useZeroQuery(
    z.query.ReviewTable.where(({ and, cmp }) => and(cmp('owner', owner), cmp('repo', repo), cmp('pullNumber', number)))
  );

  const [{ data: collaborators }, { data: requestedReviewers }, { data: reviewAgents }] = useSuspenseQueries({
    queries: [
      collaboratorsQuery(owner, repo),
      requestedReviewersQuery(owner, repo, number),
      reviewAgentsQuery(owner, repo, number),
    ],
  });

  const { modifyReviewers, assignAgentToReview } = usePullMutations(owner, repo, number);

  const authorLogin = useMemo(() => {
    return pull?.author?.login ?? '';
  }, [pull]);

  const toggleReviewer = (login: string, add: boolean) => {
    if (login === authorLogin) return;
    if (reviewAgents?.find((agent) => agent.login === login)) {
      assignAgentToReview({
        login,
        action: add ? 'add' : 'remove',
      });
      return;
    }
    modifyReviewers({
      username: login,
      action: add ? 'add' : 'remove',
    });
  };

  // Get reviewers and their last review state
  const reviewers: { login: string; avatarUrl: string; lastReviewState: string }[] = useMemo(() => {
    if (!reviews) return [];

    const grouped = Object.groupBy(
      reviews.filter((r) => r !== null),
      (r) => r.author!.login
    );

    return Object.values(grouped)
      .filter((r) => r && r.length > 0)
      .map((r) => {
        const sorted = r!.sort((a, b) => {
          const aTerminal = TERMINAL_REVIEW_STATES.includes(a.state);
          const bTerminal = TERMINAL_REVIEW_STATES.includes(b.state);
          if (aTerminal && !bTerminal) return -1;
          if (!aTerminal && bTerminal) return 1;
          return Date.parse(b!.createdAt ?? '') - Date.parse(a!.createdAt ?? '');
        });

        return {
          login: sorted[0]!.author!.login,
          avatarUrl: sorted[0]!.author!.avatarUrl,
          lastReviewState: sorted[0]!.state,
        };
      });
  }, [reviews]);

  const potentialReviewers = useMemo(() => {
    const humans =
      collaborators
        ?.filter((c) => c.login !== authorLogin)
        ?.map((c) => {
          const matchingReview = reviewers.find((r) => r.login === c.login);
          const matchingRequested = requestedReviewers?.users.find((r) => r.id === c.id);
          return {
            login: c.login,
            avatarUrl: c.avatar_url,
            lastReviewState: matchingReview?.lastReviewState as GithubReviewState | undefined,
            requested: !!matchingRequested,
            isBot: false,
          };
        }) ?? [];

    const agents =
      reviewAgents?.map((agent) => {
        const matchingReview = reviewers.find((r) => r.login === agent.login);

        return {
          login: agent.login,
          avatarUrl: agent.avatar_url,
          lastReviewState: matchingReview?.lastReviewState as GithubReviewState | undefined,
          requested: false, // TODO: support requested review agents
          isBot: true,
        };
      }) ?? [];

    return [...humans, ...agents];
  }, [collaborators, authorLogin, reviewers, reviewAgents, requestedReviewers]);

  // Check if there are new commits since the last Mesa review. we should do this in a smarter way probably.
  const hasNewCommitsSinceLastMesaReview = useMemo(() => {
    if (!reviews || !pull) return false;
    
    // Find the last Mesa review
    const mesaReviews = reviews.filter(r => 
      r?.author?.login === 'mesa-dot-dev[bot]' || 
      r?.author?.login === 'mesa-dev-development[bot]'
    );
    
    if (mesaReviews.length === 0) return false;
    
    // Sort by creation date to get the most recent
    const lastMesaReview = mesaReviews.sort((a, b) => 
      Date.parse(b?.createdAt ?? '0') - Date.parse(a?.createdAt ?? '0')
    )[0];
    
    if (!lastMesaReview?.createdAt || !pull.updatedAt) return false;
    
    // Check if PR was updated after the last Mesa review
    return Date.parse(pull.updatedAt) > Date.parse(lastMesaReview.createdAt);
  }, [reviews, pull]);

  return { potentialReviewers, toggleReviewer, hasNewCommitsSinceLastMesaReview };
}
